import React, {useMemo, useRef} from "react";
import HelpIcon from "../Help/HelpIcon";
import {Bar} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  TimeScale,
  ChartOptions,
  ChartData
} from "chart.js";
import "chartjs-adapter-date-fns";

import {chartHeight} from "../../constants";
import colors from "../../styles/colors";
import {useChartTheme} from "../../contexts/ChartThemeContext";
import {ChartErrorBoundary} from "../Charts/ChartErrorBoundary";
import {createChartDataProcessor, validateChartData} from "../../utils/chartDataProcessor";
import {alignDataToTimeline} from "../../utils/dataAlignment";
import {createTickFormatter} from "../../utils/chartTicks";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, ChartLegend, TimeScale);

interface LatencyReportProps {
  title: string;
  points: Array<[number, number, number, number, number]>; // [time, min, avg, max, n]
  axisSeries?: {
    name: string;
    data: Array<{timestamp: number; value: number}>;
    timeRange?: {start: number; end: number};
    ticks?: Array<{timestamp: number; label: string}>;
  };
}

const LatencyReport: React.FC<LatencyReportProps> = (props) => {
  const chartRef = useRef<ChartJS<"bar", {x: number; y: number}[], unknown>>(null);
  const {getThemeColors} = useChartTheme();
  const dataProcessor = createChartDataProcessor();

  // Create tick formatter with consistent formatting
  // Note: Data is pre-localized in parent ChartsReport, so we pass 0 for timezone offset
  const tickFormatter = useMemo(() => {
    return createTickFormatter(0);
  }, []);

  const chartData = useMemo(() => {
    if (!props.points || props.points.length === 0) {
      // Even with no data, create empty data points for proper time axis display
      if (props.axisSeries?.data) {
        return props.axisSeries.data.map((point) => ({
          time: point.timestamp,
          timestamp: point.timestamp,
          max: 0,
          min: 0,
          avg: 0,
          hasData: false
        }));
      }
      return [];
    }

    // If we have axisSeries, use it for proper timeline alignment
    if (props.axisSeries?.data) {
      try {
        // Latency report uses 60-second windows with max/min aggregation (matches original pondjs implementation)
        // Original: windowSize: "60s", aggregation: { max: {max: max()}, min: {min: min()} }

        // Process latency data using the shared timeline with proper 60-second aggregation
        // Process max values with max aggregation
        const maxData = alignDataToTimeline(
          props.axisSeries.data, // Use shared timeline from ChartsReport
          props.points.map((point) => [point[0], point[3]]), // [timestamp, max]
          "60s", // 60 second windows (matches original)
          "max" // Max aggregation (matches original pondjs max() function)
        );

        // Process min values with min aggregation
        const minData = alignDataToTimeline(
          props.axisSeries.data, // Use shared timeline from ChartsReport
          props.points.map((point) => [point[0], point[1]]), // [timestamp, min]
          "60s", // 60 second windows (matches original)
          "min" // Min aggregation (matches original pondjs min() function)
        );

        // Process avg values with avg aggregation
        const avgData = alignDataToTimeline(
          props.axisSeries.data,
          props.points.map((point) => [point[0], point[2]]), // [timestamp, avg]
          "60s",
          "avg"
        );

        // Merge aligned data - both arrays should have the same length since they use the same timeline
        const chartFormatData = maxData.map((point, index) => ({
          time: point.timestamp,
          timestamp: point.timestamp,
          max: point.value || 0,
          min: minData[index]?.value || 0,
          avg: avgData[index]?.value || 0,
          hasData: point.value !== null || minData[index]?.value !== null
        }));

        // Optimize for rendering
        return chartFormatData.filter((point) => point.hasData); // Only show windows with data
      } catch {
        // Fall back to original method
      }
    }

    // Fallback: Original processing method (for backward compatibility)
    const validation = validateChartData(
      props.points.map((point) => ({
        timestamp: point[0],
        value: point[3] // max value
      }))
    );

    if (!validation.isValid) {
      return [];
    }

    // Process data for 60-second aggregation windows
    const processedData = props.points.map((point) => ({
      time: point[0],
      timestamp: point[0],
      min: point[1],
      avg: point[2],
      max: point[3],
      count: point[4],
      hasData: true
    }));

    return processedData.slice(0, Math.min(processedData.length, 100)); // Limit for performance
  }, [props.points, props.axisSeries, dataProcessor]);

  const statistics = useMemo(() => {
    if (chartData.length === 0) {
      return {maxLatency: 0, minLatency: 0, hasData: false};
    }

    const maxValues = chartData.map((d) => d.max);
    const minValues = chartData.map((d) => d.min);

    return {
      maxLatency: Math.max(...maxValues),
      minLatency: Math.min(...minValues),
      hasData: true
    };
  }, [chartData]);

  const themeColors = getThemeColors();

  // Prepare data for Chart.js - grouped bar chart
  const preparedChartData = useMemo(() => {
    return chartData.map((d) => ({
      x: d.timestamp,
      yMax: d.max,
      yMin: d.min
    }));
  }, [chartData]);

  // Chart.js data configuration
  const data: ChartData<"bar", {x: number; y: number}[]> = {
    datasets: [
      {
        label: "Max Latency",
        data: preparedChartData.map((d) => ({x: d.x, y: d.yMax})),
        backgroundColor: colors.lightblue + "CC",
        borderColor: colors.lightblue,
        borderWidth: 1,
        borderRadius: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.9
      },
      {
        label: "Min Latency",
        data: preparedChartData.map((d) => ({x: d.x, y: d.yMin})),
        backgroundColor: colors.salmonpink + "CC",
        borderColor: colors.salmonpink,
        borderWidth: 1,
        borderRadius: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.9
      }
    ]
  };

  // Chart.js options configuration
  const options: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: themeColors.text,
            usePointStyle: true,
            padding: 10
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip.background,
          borderColor: themeColors.tooltip.border,
          borderWidth: 1,
          titleColor: themeColors.tooltip.text,
          bodyColor: themeColors.tooltip.text,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              const timestamp = context[0]?.parsed?.x;
              return timestamp ? new Date(timestamp).toLocaleTimeString() : "";
            },
            label: (context) => {
              const label = context.dataset.label || "";
              const value = context.parsed?.y;
              if (value !== null && value !== undefined) {
                // Find the original data point to get avg value
                const xValue = context.parsed?.x;
                if (xValue !== null && xValue !== undefined) {
                  chartData.find((d) => Math.abs(d.timestamp - xValue) < 1000);
                }
                if (label === "Max Latency") {
                  return `Max: ${Math.round(value)}ms`;
                } else if (label === "Min Latency") {
                  return `Min: ${Math.round(value)}ms`;
                }
              }
              return "";
            },
            afterLabel: (context) => {
              // Add average value to tooltip
              if (context.datasetIndex === 0) {
                // Only show once
                const xValue = context.parsed?.x;
                if (xValue !== null && xValue !== undefined) {
                  const dataPoint = chartData.find((d) => Math.abs(d.timestamp - xValue) < 1000);
                  if (dataPoint && dataPoint.avg) {
                    return `Avg: ${Math.round(dataPoint.avg)}ms`;
                  }
                }
              }
              return "";
            }
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: false
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            },
            maxTicksLimit: 20,
            autoSkip: true,
            autoSkipPadding: 10,
            callback: function (value) {
              return tickFormatter(value);
            }
          },
          min: props.axisSeries?.timeRange?.start,
          max: props.axisSeries?.timeRange?.end
        },
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: "Latency (ms)",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            }
          },
          min: 0
        }
      }
    }),
    [themeColors, props.axisSeries, tickFormatter, preparedChartData, chartData]
  );

  const hasActualData = chartData.some((d) => d.hasData !== false);

  return (
    <ChartErrorBoundary>
      <div>
        <h5>
          {props.title}
          <HelpIcon topicId="latency" />
        </h5>

        <div style={{marginBottom: "10px"}}>
          {hasActualData ?? (
            <div style={{display: "flex", gap: "20px", flexWrap: "wrap"}}>
              <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: colors.lightblue,
                    borderRadius: "2px"
                  }}
                ></div>
                <span style={{color: themeColors.text}}>
                  Max {statistics.hasData ? `${statistics.maxLatency}ms` : "--"}
                </span>
              </div>
              <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: colors.salmonpink,
                    borderRadius: "2px"
                  }}
                ></div>
                <span style={{color: themeColors.text}}>
                  Min {statistics.hasData ? `${statistics.minLatency}ms` : "--"}
                </span>
              </div>
            </div>
          )}
        </div>

        <div style={{width: "100%", height: chartHeight, position: "relative"}}>
          <Bar ref={chartRef} data={data} options={options} />
        </div>
      </div>
    </ChartErrorBoundary>
  );
};

export default LatencyReport;
